'use client';

import { useIntl } from 'react-intl';
import { wrapUrlSafe } from '@alltrails/shared/utils/languageSupport';
import useFeatures from '@alltrails/features/hooks/useFeatures';
import { useLanguageRegionCode } from '@alltrails/language/hooks/useLanguageRegionCode';
import InfoDividerDot from '@alltrails/shared/components/cards/InfoDividerDot';
import StarFilled from '@alltrails/denali/icons/StarFilled';
import PhotoSlash from '@alltrails/denali/icons/PhotoSlash';
import { AlgoliaTrail } from '@alltrails/trails';
import Image from '@alltrails/core/components/Image';
import { metersToFormattedDistance, ratingToFormattedRating } from '@alltrails/shared/utils/dataConversions';
import useDifficultyText from '@alltrails/shared/hooks/useDifficultyText';
import styles from './FeaturedTrailsModule.module.scss';
import { useSelector } from '@/utils/redux';

type FeaturedTrailsModuleCardProps = {
  trail: AlgoliaTrail;
  label?: string;
};

const EasiestTrailsToGetToCard = ({ trail, label }: FeaturedTrailsModuleCardProps) => {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const displayMetric = useSelector(state => state.context.displayMetric);
  const showStrenuousDifficultyLevel = useFeatures()?.includes('show_strenuous_difficulty_level');

  const localizedRating = ratingToFormattedRating(trail.avg_rating || 0, languageRegionCode);
  const difficultyText = useDifficultyText(parseInt(trail.difficulty_rating || '0'), showStrenuousDifficultyLevel);

  return (
    <a href={wrapUrlSafe(`/${trail.slug}`, languageRegionCode)} className={styles.card}>
      {trail.profile_photo_url ? (
        <Image className={styles.trailImage} src={trail.profile_photo_url} alt={trail.name} width={75} height={75} loading="lazy" />
      ) : (
        <div className={styles.emptyImage}>
          <PhotoSlash color="--color-text-secondary" />
        </div>
      )}
      <div className={styles.trailInfo}>
        {label && <div className={styles.typeLabel}>{label}</div>}
        <div className={styles.trailName}>{trail.name}</div>
        <div className={styles.trailDetails}>
          {!!trail.avg_rating && (
            <>
              <StarFilled color="--color-text-secondary" className={styles.star} />
              <span data-testid="rating">{localizedRating}</span>
            </>
          )}
          {!!trail.difficulty_rating && (
            <div>
              {(!!trail.avg_rating || !!trail.num_reviews) && <InfoDividerDot />}
              <span>{difficultyText}</span>
            </div>
          )}
          {!!trail.length && (
            <div className={styles.trailLength}>
              {(!!trail.difficulty_rating || !!trail.avg_rating) && <InfoDividerDot />}
              <span>{metersToFormattedDistance(intl, languageRegionCode, trail.length, displayMetric ? 'km' : 'mi', 1)}</span>
            </div>
          )}
        </div>
      </div>
    </a>
  );
};

export default EasiestTrailsToGetToCard;
